import React, {
  forwardRef,
  useLayoutEffect,
  useRef,
  useEffect,
  useState,
} from "react"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Container from "@components/structure/Container"
import ColorLines from "@homepage/ColorLines"
import Circle from "@homepage/Circle"

import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/dist/ScrollTrigger"

import tw, { css } from "twin.macro"
import {
  btnStyles,
  darkPurpleHeading,
  purpleHeading,
  paragraphStyles,
  pSuperStyles,
  buttonPurple,
} from "@styles/ui"

// temporarily replacing the video with an image until video is approved
// import howDefenCathWorksMp4 from "@videos/homepage/how-defencath-works.mp4"
// import howDefenCathWorksWebM from "@videos/homepage/how-defencath-works.webm"

const SectionTwo = forwardRef((props, ref) => {
  gsap.registerPlugin(ScrollTrigger)

  const { sectionTwoNumOneImg, sectionTwoNumTwoImg } = props

  const sec2 = ref
  const circle = useRef(null)
  const howdefWorks = useRef(null)
  const provenEfficacy = useRef(null)

  let [circleTop, setCircleTop] = useState("0px")
  useEffect(() => {
    setCircleTop(`${provenEfficacy.current.offsetTop}px`)
  }, [])

  const tl = useRef()

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      tl.current = gsap.timeline({
        scrollTrigger: {
          trigger: circle.current,
          start: "-50% 20%",
          endTrigger: howdefWorks.current,
          end: "top-=8px 20%",
          pin: circle.current,
          pinSpacing: true,
          scrub: 1,
          toggleActions: "restart none none reverse",
        },
      })
    })
    return () => ctx.revert()
  }, [])

  const pStyles = [paragraphStyles, tw`text-purple mb-4`]
  const supStyles = tw`text-[50%] top-[-0.7em]`
  const purpleTitles = [purpleHeading, tw`my-2 md:my-4`]
  const buttonStyle = [btnStyles, tw`transition-all duration-300`]

  return (
    <section
      id="sectionTwo"
      className="hp-sections"
      tw="z-20 bg-white min-h-[100vh] relative"
      ref={sec2}
    >
      <div
        id="section-two-container"
        tw="pl-[2.875rem] pr-4 py-[100px] z-[12] lg:(overflow-hidden pl-0 pr-3.5 pb-52) xl:(pr-0 )"
      >
        <div
          id="purpleLines-container-2"
          tw="top-0 h-full left-[14px] z-[12] w-5 absolute lg:(left-[1px] right-0 bottom-0 m-auto)"
        >
          <ColorLines purple />
          <div
            ref={circle}
            css={[
              css`
                top: ${circleTop};
              `,
              tw`absolute lg:(m-auto right-0)`,
            ]}
          >
            <Circle data-id="first-purple-circle" tw="bg-violet" />
          </div>
        </div>

        <Container
          data-isi-trigger="warnings"
          id="unparalleled-efficacy"
          tw="mobileOnly:(px-0 items-baseline) grid lg:(h-auto items-center grid-cols-12 gap-6)"
        >
          <div tw="pb-24 lg:(pb-0 col-start-2 col-span-4)">
            <div tw="pr-10 lg:pr-0">
              <h2 css={purpleTitles} ref={provenEfficacy}>
                PROVEN EFFICACY
              </h2>

              <h3
                css={[
                  darkPurpleHeading,
                  tw`md:pb-2 lg:(pb-0 text-[37.5px]) xl:(text-5xl)`,
                ]}
              >
                The first FDA&#8209;approved CLS proven to reduce the incidence
                of CRBSIs by&nbsp;71%<sup css={supStyles}>1</sup>
              </h3>

              <p css={pStyles}>
              Studied in the largest, randomized, double blind study to date in the U.S. in patients receiving hemodialysis via CVC. DefenCath has been proven to reduce CRBSIs.
                <sup css={pSuperStyles}>1,2</sup>
              </p>
            </div>
            <div tw="my-5 lg:(mt-4)">
              <Link
                to="/efficacy"
                css={[
                  buttonStyle,
                  tw`text-tan px-6 md:(block w-96) lg:(text-xl leading-[1.25] py-3)`,
                ]}
              >
                Explore study design and results
              </Link>
            </div>
          </div>

          <GatsbyImage
            alt=""
            loading="eager"
            tw="mobileOnly:(!w-full aspect-[145/68] !h-auto order-first rounded-xl) lg:(col-start-8 col-span-6 rounded-3xl max-w-full relative) xl:(col-start-7 col-span-6 -right-7) 2xl:-right-14"
            image={sectionTwoNumOneImg}
          />
        </Container>

        <Container
          id="how-defencath-works"
          tw="px-0 grid lg:(h-auto grid-cols-12 gap-6 pt-40 px-6)"
        >
          {/* temporarily replacing the video with an image until video is approved */}
          {/* <video autoPlay loop muted playsInline poster={sectionTwoNumTwoImg} tw="rounded-xl mobileOnly:(!w-full aspect-[16/9] !h-auto rounded-xl) lg:(col-start-1 col-span-5 max-w-full) xl:(col-span-6) 2xl:(-left-14 relative)">
            <source src={howDefenCathWorksWebM} type="video/webm" />
            <source src={howDefenCathWorksMp4} type="video/mp4" />
            Your browser does not support the video tag.
          </video> */}
          <GatsbyImage
            alt=""
            loading="eager"
            objectFit="contain"
            tw="mobileOnly:(!w-full aspect-[305/147] !h-auto rounded-xl) lg:(col-start-1 col-span-5 max-w-full) xl:(col-span-6 -left-14) 2xl:(relative)"
            image={sectionTwoNumTwoImg}
          />

          <div tw="pr-10 lg:(pr-0 col-start-8 col-span-4)">
            <h2 ref={howdefWorks} css={[purpleTitles, tw`block`]}>
              HOW DEFENCATH WORKS
            </h2>

            <h3
              css={[
                darkPurpleHeading,
                tw`md:pb-2 lg:(pb-0 text-[32px]) xl:(text-5xl)`,
              ]}
            >
              A first-in-class CLS&nbsp;with taurolidine&nbsp;+ heparin
              <sup css={supStyles}>1</sup>
            </h3>

            <p css={[pStyles]}>
              DefenCath is the only FDA-approved solution that protects the CVC
              lumen with antimicrobial activity.
              <sup css={pSuperStyles}>1</sup>
            </p>

            <Link
              to="/how-defencath-works"
              css={[
                buttonPurple,
                tw`text-tan w-[159px] my-5 py-[0.375rem] md:(block w-[201px]) lg:(mt-5 text-xl leading-[1.25])`,
              ]}
            >
              Explore MOA
            </Link>
          </div>
        </Container>
      </div>
    </section>
  )
})

export default SectionTwo
